/**
 * Database types + helpers.
 * Per app-pact/state.md the schema is:
 *   members, allowlist, tasks, gamification, messages, resources
 * All state lives in Supabase Postgres. RLS enforces channel scoping + approval.
 */
import { supabase } from './supabase'
import type { Role, Subtype, ChatChannel } from './store'

export interface DbMember {
  id: string
  name: string
  avatar_url: string | null
  role: Role
  subtype: Subtype
  ep_opt_out: boolean
  created_at: string
  deleted_at: string | null
}

export type RecurrenceRule = 'none' | 'daily' | 'weekdays' | 'weekly' | 'biweekly' | 'monthly'

export interface DbTask {
  id: string
  title: string
  type: string
  priority: 'low' | 'med' | 'high' | 'urgent'
  ep: number
  due: string | null
  assignees: string[]
  status: 'todo' | 'in_progress' | 'pending_approval' | 'done' | 'archived'
  position: number
  created_by: string
  created_at: string
  updated_at: string
  done_at: string | null
  archived_at: string | null
  // Recurrence — when the task is part of a recurring series, this is the rule
  // and recurrence_parent_id points to the original template task.
  recurrence: RecurrenceRule
  recurrence_parent_id: string | null
  // Approval gate: a task in 'pending_approval' has earned EP for its assignees
  // but the EP has not been credited to them yet (parent approval flow).
}

export interface DbGamification {
  member_id: string
  ep: number  // Confirmed, parent-approved EP
  pending_ep: number  // Earned but not yet approved
  lifetime_ep: number  // Total EP ever earned (for stats)
  level: number
  streak_days: number
  last_streak_date: string | null
  achievements: string[]
}

export interface DbMessage {
  id: string
  channel: ChatChannel
  dm_with: string | null
  sender: string
  body: string
  created_at: string
}

export interface DbResource {
  id: string
  title: string
  description: string | null
  type: 'article' | 'tool' | 'link' | 'file'
  subjects: string[]
  tags: string[]
  file_url: string | null
  created_by: string
  created_at: string
  updated_at: string
}

export interface DbAllowlist {
  email: string
}

// === Members ===

export async function fetchMembers() {
  const { data, error } = await supabase
    .from('members')
    .select('*')
    .is('deleted_at', null)
    .order('name')
  return { data: data as DbMember[] | null, error }
}

export async function updateMember(memberId: string, updates: Partial<DbMember>) {
  return supabase.from('members').update(updates).eq('id', memberId)
}

export async function inviteEmail(email: string) {
  return supabase.from('allowlist').insert({ email })
}

// === Tasks ===

export async function fetchTasks(familyId: string) {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .or(`assignees.cs.["${familyId}"]`)
    .neq('status', 'archived')
    .order('position')
  return { data: data as DbTask[] | null, error }
}

export async function createTask(task: Partial<DbTask>) {
  return supabase.from('tasks').insert(task).select().single()
}

/**
 * Compute the next due date for a recurring task based on the current due date.
 * Pure function — testable in isolation.
 */
export function nextRecurrenceDue(rule: RecurrenceRule, currentDue: string | null): string | null {
  if (rule === 'none' || !currentDue) return null
  const d = new Date(currentDue)
  if (Number.isNaN(d.getTime())) return null
  switch (rule) {
    case 'daily':
      d.setDate(d.getDate() + 1)
      break
    case 'weekdays': {
      d.setDate(d.getDate() + 1)
      // Skip Sat (6) and Sun (0)
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1)
      break
    }
    case 'weekly':
      d.setDate(d.getDate() + 7)
      break
    case 'biweekly':
      d.setDate(d.getDate() + 14)
      break
    case 'monthly':
      d.setMonth(d.getMonth() + 1)
      break
  }
  return d.toISOString()
}

export async function updateTaskStatus(taskId: string, status: DbTask['status']) {
  return supabase
    .from('tasks')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', taskId)
}

export async function updateTask(taskId: string, updates: Partial<DbTask>) {
  return supabase.from('tasks').update(updates).eq('id', taskId)
}

export async function moveTask(taskId: string, status: DbTask['status'], position: number) {
  return supabase
    .from('tasks')
    .update({ status, position, updated_at: new Date().toISOString() })
    .eq('id', taskId)
}

// === Gamification ===

export async function fetchGamification(memberId: string) {
  const { data, error } = await supabase
    .from('gamification')
    .select('*')
    .eq('member_id', memberId)
    .single()
  return { data: data as DbGamification | null, error }
}

export async function awardEp(memberId: string, ep: number) {
  const { data: current } = await fetchGamification(memberId)
  const newEp = (current?.ep ?? 0) + ep
  return supabase
    .from('gamification')
    .upsert({ member_id: memberId, ep: newEp, level: levelForEp(newEp) })
}

export async function updateStreak(memberId: string, streakDays: number, lastDate: string) {
  return supabase
    .from('gamification')
    .upsert({ member_id: memberId, streak_days: streakDays, last_streak_date: lastDate })
}

export async function addAchievement(memberId: string, achievementId: string) {
  const { data: current } = await fetchGamification(memberId)
  const achievements = current?.achievements ?? []
  if (achievements.includes(achievementId)) return
  return supabase
    .from('gamification')
    .upsert({ member_id: memberId, achievements: [...achievements, achievementId] })
}

export function levelForEp(ep: number): number {
  const thresholds = [0, 100, 250, 500, 1000, 1750, 2750, 4000, 6000, 10000]
  let level = 1
  for (let i = 0; i < thresholds.length; i++) {
    if (ep >= thresholds[i]) level = i + 1
  }
  return level
}

export const LEVEL_NAMES = [
  'Sower', 'Sprout', 'Green Shoot', 'Bearer', 'Harvester',
  'Steward', 'Keeper', 'Builder', 'Pillar', 'Elder',
]

export const LEVEL_THRESHOLDS = [0, 100, 250, 500, 1000, 1750, 2750, 4000, 6000, 10000]

// === Messages (chat) ===

export async function fetchMessages(channel: ChatChannel, dmWith?: string | null) {
  let q = supabase.from('messages').select('*').eq('channel', channel).order('created_at', { ascending: true })
  if (channel === 'dm' && dmWith) {
    q = q.or(`sender.eq.${dmWith},dm_with.eq.${dmWith}`)
  }
  return q
}

export async function sendMessage(channel: ChatChannel, body: string, sender: string, dmWith?: string | null) {
  return supabase.from('messages').insert({
    channel,
    dm_with: channel === 'dm' ? dmWith : null,
    sender,
    body,
  })
}

export function subscribeMessages(onMessage: (msg: DbMessage) => void, channel?: ChatChannel) {
  return supabase
    .channel(`messages:${channel ?? 'all'}`)
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
      onMessage(payload.new as DbMessage)
    })
    .subscribe()
}
export function unsubscribeMessages(sub: any) {
  if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe()
}

// === Resources ===

export async function fetchResources() {
  const { data, error } = await supabase
    .from('resources')
    .select('*')
    .order('created_at', { ascending: false })
  return { data: data as DbResource[] | null, error }
}

export async function createResource(resource: Partial<DbResource>) {
  return supabase.from('resources').insert(resource).select().single()
}
